import React, { type ComponentProps, type FC, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Truck } from 'react-bootstrap-icons';

import { useGetCartDeliveryInfo, useGetProductExport } from 'api';
import { useSelector } from 'store';
import { useNotifications } from 'hooks';

import LegacyDetails from 'pages/Product/components/Details';
import { Availability } from 'components/controls';
import { AddToShoppingListButton } from 'components/containers';
import WiseAvailability from 'components/controls/wise/WiseAvailability/WiseAvailability';
import WiseActionButton from 'components/wise/WiseActionButton';
import { WiseAskProductModal } from 'components/wise';
import { WiseAddToShoppingListButton } from 'components/wise';

import pdpActionStyles from 'theme/components/wise/WisePdpActionButton/WisePdpActionButton.module.scss';
import styles from 'theme/pages/wise/WiseProduct/components/WiseDetails/WiseDetails.module.scss';

export type WiseDetailsProps = ComponentProps<typeof LegacyDetails>;

const WiseDetails: FC<WiseDetailsProps> = ({ product, ...rest }) => {
  const { profile } = useSelector((state) => state.auth);
  const { currentCartId } = useSelector((state) => state.cart);
  const { showWarningMessage } = useNotifications();
  const isOpenProfile = profile?.role === 'ROLE_OPEN_PROFILE';
  const [askOpen, setAskOpen] = useState(false);

  const { data: cartDeliveryInfoData } = useGetCartDeliveryInfo(currentCartId || 0, {
    enabled: !!currentCartId && profile?.role !== 'ROLE_OPEN_PROFILE'
  });

  const { mutate: exportProductPdf } = useGetProductExport({
    onSuccess: (data) => {
      const a = document.createElement('a');
      a.href = `data:application/pdf;base64,${data.content}`;
      a.download = data.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    onError: () => {
      showWarningMessage('Nie udało się pobrać karty produktu');
    }
  });

  const defaultUnit = useMemo(
    () => product?.product_price_full_info?.prices?.[0],
    [product]
  );

  const favouriteUnitId =
    defaultUnit?.unit_id ?? product?.product_unit_short_info?.default_unit_id ?? null;

  const listQuantity = defaultUnit?.minimal_quantity || 1;

  return (
    <div className={styles.wiseDetailsRoot}>
      <LegacyDetails product={product} {...rest} />
      <div
        className={classnames(
          styles.detailsInfo,
          'StylePath-Pages-Wise-WiseProduct-components-WiseDetails-detailsInfo'
        )}>
        <div className={styles.detailsInfoItem}>
          {product.product_stock_formatted_full_info && (
            <WiseAvailability stock={product.product_stock_formatted_full_info} />
          )}
        </div>
        {cartDeliveryInfoData?.info && (
          <div className={styles.detailsInfoItem}>
            <Truck />
            {cartDeliveryInfoData.info}
          </div>
        )}
      </div>
      <div
        className={classnames(
          styles.detailsAction,
          'StylePath-Pages-Wise-WiseProduct-components-WiseDetails-detailsAction'
        )}>
        <WiseActionButton variant="print" onClick={() => exportProductPdf(product.id)} />
        {favouriteUnitId != null && (
          <WiseActionButton
            variant="favourite"
            product={product}
            unitId={favouriteUnitId}
            className="StylePath-Pages-Wise-WiseProduct-components-WiseDetails-favourite"
          />
        )}
        {!isOpenProfile && defaultUnit && (
          <WiseAddToShoppingListButton
            product_id={product.id}
            unit_id={defaultUnit.unit_id}
            image={product?.product_image_formatted_full_info?.images?.[0]?.thumb}
            title={product?.product_basic_full_info?.title}
            index={product?.product_basic_full_info?.index}
            currency={defaultUnit?.currency}
            quantity={listQuantity}
            unit={defaultUnit}
            triggerClassName={pdpActionStyles.actionButton}
          />
        )}
        <WiseActionButton
          variant="askProduct"
          product={product}
          onClick={() => setAskOpen(true)}
          className="StylePath-Pages-Wise-WiseProduct-components-WiseDetails-askProduct"
        />

        <WiseAskProductModal product={product} open={askOpen} onClose={() => setAskOpen(false)} />
      </div>
    </div>
  );
};

export default WiseDetails;
